/**
 * The one scroller, addressable.
 *
 * **There is exactly one scroll container in the product** — `.app-screen`, mounted by `App` —
 * because D132 took the scroll off the document. That is what makes a module-level handle correct
 * here rather than lazy: there is nothing to disambiguate, no second scroller a later screen could
 * introduce without changing `global.css`'s single `overflow-y` rule, and the two callers that need
 * it (the app-bar title and the iOS status-bar bridge) are both chrome that renders outside any
 * screen's subtree.
 *
 * **Why not a ref through context.** Both callers are P6 shell, one of them is not a React tree at
 * all — `installScrollToTopBridge()` runs beside `installPortraitLock()`, before the first render —
 * and threading a ref to it would mean a provider whose only consumer is a `window` listener.
 */

let scroller: HTMLElement | undefined

/**
 * `App` registers the node it renders. Passing `null` (a React ref callback on unmount) clears it,
 * so a stale detached node can never be scrolled.
 */
export function setShellScroller(el: HTMLElement | null): void {
  scroller = el ?? undefined
}

export function getShellScroller(): HTMLElement | undefined {
  return scroller
}

/**
 * Scrolls the shell to the top. A no-op with nothing registered — which is the correct answer for
 * the two moments it can happen (before the first render, and on the auth screen), and the reason
 * this never throws.
 *
 * `behavior: 'auto'` rather than `'smooth'`: the status-bar tap is a platform affordance whose
 * whole value is immediacy, and `prefers-reduced-motion` would otherwise need a branch here.
 */
export function scrollShellToTop(): void {
  scroller?.scrollTo({ top: 0, behavior: 'auto' })
}

/** The event the native container dispatches. Named once; the Swift side sends this string. */
export const SCROLL_TO_TOP_EVENT = 'selogate:scroll-to-top'

/**
 * **The iOS status-bar tap, and the only thing on the web side of it.**
 *
 * iOS routes a status-bar tap to the `UIScrollView` that has `scrollsToTop`. `.app-screen` is a CSS
 * overflow container painted *inside* the web content — never a `UIScrollView` — and the document
 * has nothing to scroll, so the tap resolves to a scroll view already at offset zero and nothing
 * happens. The container's view controller intercepts it and dispatches this event instead.
 *
 * **Installed unconditionally, on every platform.** In a browser and on Android nothing ever
 * dispatches it, which costs one idle listener; gating the install on a container check would put a
 * platform test in front of a listener that is inert without one. Never torn down.
 */
export function installScrollToTopBridge(target: Pick<Window, 'addEventListener'> = window): void {
  target.addEventListener(SCROLL_TO_TOP_EVENT, () => scrollShellToTop())
}
